import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown } from 'lucide-react';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/58ujAQtekwQT37CqH9LFPA/prime_nation_transparent_bg.png';
interface NavItem {
  name: string;
  href: string;
  children?: {
    name: string;
    href: string;
  }[];
}
const navLinks: NavItem[] = [{
  name: 'ABOUT',
  href: '#about',
  children: [{
    name: 'Members',
    href: '#members'
  }]
}, {
  name: 'WORK WITH US',
  href: '#work-with-us'
}, {
  name: 'SERVICES',
  href: '#services',
  children: [{
    name: 'Do I Qualify',
    href: '#qualify'
  }, {
    name: 'Schedule Appointment',
    href: '#contact'
  }]
}, {
  name: 'GALLERY',
  href: '#gallery'
}, {
  name: 'PRESS & NEWS',
  href: '#press'
}, {
  name: 'CONTACT',
  href: '#contact',
  children: [{
    name: 'Locations',
    href: '#locations'
  }]
}];
function DesktopDropdown({
  item
}: {
  item: NavItem;
}) {
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setOpen(true);
  };
  const handleLeave = () => {
    timeoutRef.current = setTimeout(() => setOpen(false), 150);
  };
  if (!item.children) {
    return <a href={item.href} className="text-white text-sm font-medium tracking-wider hover:text-brand-gold transition-colors" data-id="element-88">
        {item.name}
      </a>;
  }
  return <div className="relative" onMouseEnter={handleEnter} onMouseLeave={handleLeave} data-id="element-89">
      <a href={item.href} className="text-white text-sm font-medium tracking-wider hover:text-brand-gold transition-colors flex items-center gap-1" data-id="element-90">
        {item.name}
        <ChevronDown size={14} className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`} data-id="element-91" />
      </a>
      <AnimatePresence data-id="element-92">
        {open && <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 8
      }} transition={{
        duration: 0.15
      }} className="absolute top-full left-0 mt-2 bg-brand-black border border-white/10 rounded-lg shadow-xl min-w-[200px] py-2 z-50" data-id="element-93">
            {item.children.map(child => <a key={child.name} href={child.href} className="block px-5 py-3 text-white/80 text-sm font-medium tracking-wide hover:text-brand-gold hover:bg-white/5 transition-colors" data-id="element-94">
                {child.name}
              </a>)}
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function MobileNavItem({
  item,
  onClose
}: {
  item: NavItem;
  onClose: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  if (!item.children) {
    return <a href={item.href} onClick={onClose} className="flex items-center px-6 py-4 text-white text-lg font-medium tracking-wider hover:text-brand-gold transition-colors border-b border-[#D4A24C]/20" data-id="element-95">
        {item.name}
      </a>;
  }
  return <div className="border-b border-[#D4A24C]/20" data-id="element-96">
      <button onClick={() => setExpanded(!expanded)} className="flex items-center justify-between w-full px-6 py-4 text-white text-lg font-medium tracking-wider hover:text-brand-gold transition-colors" data-id="element-97">
        {item.name}
        <ChevronDown size={18} className={`text-brand-gold transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} data-id="element-98" />
      </button>
      <AnimatePresence data-id="element-99">
        {expanded && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="overflow-hidden" data-id="element-100">
            <div className="pl-10 pb-3 flex flex-col" data-id="element-101">
              {item.children.map(child => <a key={child.name} href={child.href} onClick={onClose} className="text-white/60 text-base font-medium hover:text-brand-gold transition-colors py-3 flex items-center" data-id="element-102">
                  {child.name}
                </a>)}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  return <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow] duration-300 ${scrolled ? 'bg-brand-black shadow-lg' : 'bg-brand-black/90 backdrop-blur-sm'}`} data-id="element-103">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-104">
          <div className="flex justify-between items-center h-[60px] md:h-[64px]" data-id="element-105">
            {/* Logo */}
            <a href="#" className="flex items-center gap-2 shrink-0" data-id="element-106">
              <img src={LOGO_URL} alt="Prime Nation Logo" className="h-10 md:h-12 w-auto object-contain brightness-0 invert" data-id="element-107" />
            </a>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-6" data-id="element-108">
              {navLinks.map(link => <DesktopDropdown key={link.name} item={link} data-id="element-109" />)}
              <button className="bg-brand-gold text-brand-black px-6 py-2.5 rounded font-semibold text-sm hover:bg-yellow-500 transition-colors min-h-[44px]" data-id="element-110">
                Apply Now
              </button>
            </nav>

            {/* Mobile Menu Toggle */}
            <button className="lg:hidden text-white p-2 min-h-[44px] min-w-[44px] flex items-center justify-center" onClick={() => setIsOpen(true)} aria-label="Open menu" data-id="element-111">
              <Menu size={28} data-id="element-112" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Full-Screen Overlay — outside header to avoid backdrop-filter stacking context issues */}
      <AnimatePresence data-id="element-113">
        {isOpen && <motion.div initial={{
        opacity: 1
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15
      }} className="fixed inset-0 z-[60] bg-[#0A0A0A] overflow-y-auto lg:hidden" data-id="element-114">
            {/* Header bar */}
            <div className="flex justify-between items-center h-[60px] px-4 bg-[#0A0A0A]" data-id="element-115">
              <a href="#" className="flex items-center gap-2 shrink-0" onClick={() => setIsOpen(false)} data-id="element-116">
                <img src={LOGO_URL} alt="Prime Nation Logo" className="h-10 w-auto object-contain brightness-0 invert" data-id="element-117" />
              </a>
              <button onClick={() => setIsOpen(false)} className="text-white p-2 min-h-[44px] min-w-[44px] flex items-center justify-center" aria-label="Close menu" data-id="element-118">
                <X size={28} data-id="element-119" />
              </button>
            </div>

            {/* Menu Items */}
            <div className="flex flex-col pt-4" data-id="element-120">
              {navLinks.map(link => <MobileNavItem key={link.name} item={link} onClose={() => setIsOpen(false)} data-id="element-121" />)}
              <div className="px-6 pt-8 pb-6" data-id="element-122">
                <button className="bg-brand-gold text-brand-black w-full py-4 rounded font-bold text-base min-h-[48px]" data-id="element-123">
                  Apply Now
                </button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </>;
}